import React from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View } from "~/components/View";
import { useIsKeyboardVisible } from "~/hooks/useIsKeyboardVisible";
import { styleConstants as C } from "~/style/styleConstants";
import { styleConstantsV2 as C2 } from "~/style/styleConstantsV2";
import type { ReactNode } from "react";
import type { ScrollViewProps, ViewProps } from "react-native";

export const headerHeight = 52;

const S = StyleSheet.create({
  base: { backgroundColor: C.colorBackgroundLight, flex: 1 },
  contentContainer: {
    flexGrow: 1,
    backgroundColor: C.colorBackgroundLight,
  },
  flex: { flex: 1 },
});

export type ScreenProps = (
  | ({ preventScroll?: true } & ViewProps)
  | ({ preventScroll?: false } & ScrollViewProps)
) & {
  children: ReactNode;

  colorBackgroundWhite?: boolean;
  colorBackgroundLight?: boolean;
  colorBackgroundLightDark?: boolean;
  colorBackgroundLightDarker?: boolean;
  colorBackgroundTheme?: boolean;
  colorBackgroundDarkLighter?: boolean;
  colorBackgroundDarkLight?: boolean;
  colorBackgroundDark?: boolean;
  colorBackgroundBlack?: boolean;
  colorBackgroundDanger?: boolean;
  colorBackgroundWarning?: boolean;
  colorBackgroundSuccess?: boolean;
  colorBackgroundGray?: boolean;
  colorBackgroundDisabled?: boolean;
  colorBackgroundBackdrop?: boolean;

  withBottomInset: boolean;
  withTopInset: boolean;
};

export const Screen = React.forwardRef<ScrollView | View, ScreenProps>(
  (
    {
      preventScroll = false,
      // @ts-ignore TODO: Check this
      contentContainerStyle: inheritedContentContainerStyle,
      style,
      ...props
    },
    ref,
  ) => {
    const insets = useSafeAreaInsets();
    const insetBottom = insets.bottom;
    const insetTop = insets.top;

    const resolveBackgroundColor = () => {
      if (props.colorBackgroundWhite) return C.colorBackgroundWhite;
      if (props.colorBackgroundLight) return C.colorBackgroundLight;
      if (props.colorBackgroundLightDark) return C.colorBackgroundLightDark;
      if (props.colorBackgroundLightDarker) return C.colorBackgroundLightDarker;
      if (props.colorBackgroundTheme) return C.colorBackgroundTheme;
      if (props.colorBackgroundDarkLighter) return C.colorBackgroundDarkLighter;
      if (props.colorBackgroundDarkLight) return C.colorBackgroundDarkLight;
      if (props.colorBackgroundDark) return C.colorBackgroundDark;
      if (props.colorBackgroundBlack) return C.colorBackgroundBlack;
      if (props.colorBackgroundDanger) return C.colorBackgroundDanger;
      if (props.colorBackgroundWarning) return C.colorBackgroundWarning;
      if (props.colorBackgroundSuccess) return C.colorBackgroundSuccess;
      if (props.colorBackgroundGray) return C.colorBackgroundGray;
      if (props.colorBackgroundDisabled) return C.colorBackgroundDisabled;
      if (props.colorBackgroundBackdrop) return C.colorBackgroundBackdrop;

      return C2.colorTertiaryBg;
    };

    const { isKeyboardVisible } = useIsKeyboardVisible();

    const resolvePaddingBottom = () => {
      if (isKeyboardVisible) return undefined;
      if (props.withBottomInset) return insetBottom;
      return undefined;
    };

    const resolvePaddingTop = () => {
      if (props.withTopInset) return insetTop;
      return undefined;
    };

    const backgroundColor = resolveBackgroundColor();
    const paddingBottom = resolvePaddingBottom();
    const paddingTop = resolvePaddingTop();

    let renderedContent;
    if (preventScroll) {
      const screenStyle = [
        S.base,
        {
          backgroundColor,
          paddingBottom,
          paddingTop,
        },
        style,
      ];
      renderedContent = (
        <View ref={ref as React.Ref<View>} style={screenStyle} {...props} />
      );
    } else {
      const screenStyle = [S.base, { backgroundColor }, style];
      const contentContainerStyle = [
        S.contentContainer,
        { backgroundColor, paddingBottom, paddingTop },
        inheritedContentContainerStyle,
      ];

      renderedContent = (
        <ScrollView
          ref={ref as React.Ref<ScrollView>}
          style={screenStyle}
          contentContainerStyle={contentContainerStyle}
          keyboardShouldPersistTaps="handled"
          {...props}
        />
      );
    }

    return (
      <KeyboardAvoidingView
        keyboardVerticalOffset={
          props.withTopInset ? 0 : insets.top + headerHeight
        }
        enabled={Platform.select({ android: false, default: true })}
        behavior="padding"
        style={S.flex}
      >
        {renderedContent}
      </KeyboardAvoidingView>
    );
  },
);

export type Screen = typeof Screen;
